// src/components/CourierSelectionList.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { getCouriersByDistrict } from '../services/courierSelectionService';
import CourierDetailModal from './CourierDetailModal';
import './CourierSelectionList.css';

/**
 * Modal with list of available couriers for a district
 * User clicks a courier card to see details and select it
 * 
 * @param {string} districtCode - Seller's district
 * @param {boolean} isOpen - Modal visibility
 * @param {Function} onClose - Close callback
 * @param {Function} onSelectCourier - When courier is selected, passes courier object
 */
const CourierSelectionList = ({ districtCode, isOpen, onClose, onSelectCourier }) => {
  const [couriers, setCouriers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCourier, setSelectedCourier] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const fetchCouriers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔍 Fetching couriers for district:', districtCode);
      const options = await getCouriersByDistrict(districtCode);
      console.log('✅ Fetched couriers:', options);
      setCouriers(options);

      if (options.length === 0) {
        setError(`No courier services available in ${districtCode}`);
      }
    } catch (err) {
      console.error('❌ Error fetching couriers:', err);
      setError(`Failed to load courier options: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [districtCode]);

  // Fetch couriers when modal opens
  useEffect(() => {
    if (isOpen && districtCode) {
      fetchCouriers();
    }
  }, [isOpen, districtCode, fetchCouriers]);

  const handleCourierClick = (courier) => {
    console.log('✓ Courier selected:', courier);
    setSelectedCourier(courier);
  };

  const handleConfirmSelection = () => {
    if (selectedCourier && onSelectCourier) {
      onSelectCourier(selectedCourier);
    }
    onClose();
  };

  const handleViewDetails = (courier) => {
    setSelectedCourier(courier);
    setShowDetailModal(true);
  };

  const handleSelectFromDetail = (courier) => {
    if (onSelectCourier) {
      onSelectCourier(courier);
    }
    onClose();
  };

  console.log('🎯 CourierSelectionList rendered. isOpen:', isOpen, 'districtCode:', districtCode);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* List Modal */}
      <div className="courier-selection-backdrop" onClick={onClose} />
      <div className="courier-selection-modal">
        {/* Drawer Handle */}
        <div className="drawer-handle"></div>

        {/* Header */}
        <div className="courier-selection-header">
          <h2>🚚 Select Courier</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Content */}
        <div className="courier-selection-content">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading couriers for {districtCode}...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p className="error-message">⚠️ {error}</p>
              <button className="retry-btn" onClick={fetchCouriers}>
                Retry
              </button>
            </div>
          ) : couriers.length === 0 ? (
            <div className="empty-state">
              <p>No couriers available in {districtCode}</p>
            </div>
          ) : (
            <div className="couriers-grid">
              {couriers.map((courier) => (
                <CourierCard
                  key={`${courier.courierId}-${courier.branchId}`}
                  courier={courier}
                  isSelected={selectedCourier?.branchId === courier.branchId}
                  onSelect={handleCourierClick}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer with Action Buttons */}
        <div className="courier-selection-footer">
          <button
            className="btn-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="btn-select-confirm"
            onClick={handleConfirmSelection}
            disabled={!selectedCourier}
            title={selectedCourier ? 'Confirm selection' : 'Select a courier first'}
          >
            ✓ Select Courier
          </button>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedCourier && (
        <CourierDetailModal
          courier={selectedCourier}
          isOpen={showDetailModal}
          onClose={() => setShowDetailModal(false)}
          onSelect={handleSelectFromDetail}
        />
      )}
    </>
  );
};

/**
 * Compact courier card with logo and name - selectable
 */
const CourierCard = ({ courier, isSelected, onSelect, onViewDetails }) => {
  const [logoError, setLogoError] = useState(false);

  return (
    <div 
      className={`courier-card ${isSelected ? 'selected' : ''}`}
      onClick={() => onSelect(courier)}
    >
      {/* Logo Section */}
      <div className="card-logo-section">
        {courier.courierLogo && !logoError ? (
          <img
            src={courier.courierLogo}
            alt={courier.courierName}
            className="courier-logo-img"
            onError={() => setLogoError(true)}
          />
        ) : (
          <div className="courier-logo-fallback">
            {courier.courierName?.charAt(0)?.toUpperCase() || 'C'}
          </div>
        )}
      </div>

      {/* Courier Info */}
      <div className="card-info">
        <div className="card-courier-name">{courier.courierName}</div>
        <div className="card-branch-code">{courier.branchCode}</div>
        <div className="card-contact-number">📞 {courier.contactNumber || 'N/A'}</div>
      </div>

      {/* Selection Indicator */}
      {isSelected && (
        <div className="card-selected-indicator">
          <div className="checkmark">✓</div>
        </div>
      )}

      {/* View Details Button */}
      <button
        className="btn-view-details-only"
        onClick={(e) => {
          e.stopPropagation();
          if (onViewDetails) {
            onViewDetails(courier);
          }
        }}
        title="View full details"
      >
        ℹ️ Details
      </button>
    </div>
  );
};

export default CourierSelectionList;
